import css from "./notAuthMain.module.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { useEffect, useState, useRef } from "react";
import { trackDemoStarted, trackOnboardingStart, trackTrialStarted } from "../../lib/analytics";

const NewFirstBlock = ({ authUser }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const hasInteractedRef = useRef(false);
  const autoScrollTimeoutRef = useRef(null);
  
  // Neural canvas refs
  const canvasRef = useRef(null);
  const heroRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const rafRef = useRef(null);

  const markAsInteracted = () => {
    hasInteractedRef.current = true;
    sessionStorage.setItem("userHasInteracted", "true");
  };

  const handleNavigation = (path) => {
    markAsInteracted();
    navigate(path);
  };

  // Neural Network Canvas Animation
  useEffect(() => {
    const canvas = canvasRef.current;
    const hero = heroRef.current;
    if (!canvas || !hero) return;

    const ctx = canvas.getContext('2d');
    const PARTICLE_COUNT = 55;
    const MAX_DIST = 140;
    const BASE_SPEED = 0.25;
    let W, H;

    class Particle {
      constructor() {
        this.x = Math.random() * W;
        this.y = Math.random() * H;
        this.vx = (Math.random() - 0.5) * BASE_SPEED;
        this.vy = (Math.random() - 0.5) * BASE_SPEED;
        this.r = Math.random() * 1.5 + 0.5;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        if (this.x < 0 || this.x > W) this.vx *= -1;
        if (this.y < 0 || this.y > H) this.vy *= -1;
      }
    }

    const resize = () => {
      W = canvas.width = hero.offsetWidth;
      H = canvas.height = hero.offsetHeight;
    };

    const init = () => {
      resize();
      particlesRef.current = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        particlesRef.current.push(new Particle());
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      const particles = particlesRef.current;
      const mouse = mouseRef.current;

      // Draw lines between particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < MAX_DIST) {
            const alpha = (1 - dist / MAX_DIST) * 0.08;
            ctx.strokeStyle = `rgba(99, 102, 241, ${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      for (const p of particles) {
        p.update();
        const dx = mouse.x - p.x;
        const dy = mouse.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const highlight = dist < 180 ? (1 - dist / 180) * 0.35 : 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(99, 102, 241, ${0.15 + highlight})`;
        ctx.fill();

        if (highlight > 0) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r + 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(99, 102, 241, ${highlight * 0.3})`;
          ctx.fill();
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    // Mouse interaction
    const handleMouseMove = (e) => {
      const rect = hero.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
    };

    const handleMouseLeave = () => {
      mouseRef.current.x = -1000;
      mouseRef.current.y = -1000;
    };

    // Resize handler
    const handleResize = () => {
      resize();
      // Reposition particles that are out of bounds
      for (const p of particlesRef.current) {
        if (p.x > W) p.x = W;
        if (p.y > H) p.y = H;
      }
    };

    hero.addEventListener('mousemove', handleMouseMove);
    hero.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('resize', handleResize);

    init();
    draw();

    return () => {
      hero.removeEventListener('mousemove', handleMouseMove);
      hero.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('resize', handleResize);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Scroll Reveal Animation
  useEffect(() => {
    const revealEls = document.querySelectorAll(`.${CSS.escape(css.reveal)}`);
    
    const revealObs = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add(css.vis);
          revealObs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -30px 0px' });

    revealEls.forEach(el => revealObs.observe(el));

    return () => {
      revealEls.forEach(el => revealObs.unobserve(el));
    };
  }, []);







  return (
    <main className={css.firstBlockNew}>
      
<div className={css.gridBg}></div>


<section className={css.hero} ref={heroRef}>
  <canvas id={css.neuralCanvas} ref={canvasRef}></canvas>
  <div className={`${css.orb}  ${css['orb-1']}`}></div>
  <div className={`${css.orb}  ${css['orb-2']}`}></div>
  <div className={`${css.orb}  ${css['orb-3']}`}></div>

  <div className={css.heroContent}>
    <div className={css.heroBadge}>
      <span className={css.badgeDot}>
        <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
      </span>
      {t("AI-powered English platform")}
    </div>

    <h1>{t("Learn English with")} <span className={css.grad}>{t("real-life videos")}</span> {t("and AI practice")}</h1>

    <p className={css.heroSub}>{t("Choose a quick lesson, get a personal plan, or learn freely at your own pace.")}</p>

    <div className={css.flowPipeline}>
      <div className={css.flowNode}>
        <div className={`${css.nodeIcon} ${css.niVideo}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </div>
        <span className={css.nodeLabel}>{t("Video")}</span>
      </div>

      <div className={css.flowConnector}>
        <div className={css.lineBG}></div>
        <div className={css.lineGlow}></div>
        <div className={css.dotPulse}></div>
      </div>

      <div className={css.flowNode}>
        <div className={`${css.nodeIcon} ${css.niWorks}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>
        </div>
        <span className={css.nodeLabel}>{t("Words")}</span>
      </div>

      <div className={css.flowConnector}>
        <div className={css.lineBG}></div>
        <div className={css.lineGlow}></div>
        <div className={css.dotPulse}></div>
      </div>

      <div className={css.flowNode}>
        <div className={`${css.nodeIcon} ${css.niQuiz}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </div>
        <span className={css.nodeLabel}>{t("Quiz")}</span>
      </div>

      <div className={css.flowConnector}>
        <div className={css.lineBG}></div>
        <div className={css.lineGlow}></div>
        <div className={css.dotPulse}></div>
      </div>

      <div className={css.flowNode}>
        <div className={`${css.nodeIcon} ${css.niSpeak}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
        </div>
        <span className={css.nodeLabel}>{t("Speaking")}</span>
      </div>
    </div>
  </div>
</section>


<section className={css.cardsSection}>
  <div className={css.sectionLabel}>{t("Choose how to start")}</div>
  <div className={css.cardsGrid}>


    <div className={css.card}>
      <div className={css.cardInner}>
        <div className={` ${css.cardIcon} ${css.ciQuick}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        </div>
        <div className={css.cardMeta}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/></svg>
          {t("2 minutes · No signup")}
        </div>
        <h3>{t("Try a 2-minute lesson")}</h3>
        <p className={css.desc}>{t("Take a short real-life lesson and see how AI Lingua works — zero commitment.")}</p>
        <button className={` ${css.cta} ${css.ctaGhost}`}
        onClick={() => {
            trackDemoStarted();
            handleNavigation("/lesson/774");
          }}>
          {t("Start quick lesson")}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
        </button>
      </div>
    </div>

    <div className={` ${css.card} ${css.primary}`}>
      <div className={css.cardBG}></div>
      <span className={css.cardTag}>★ {t("Recommended")}</span>
      <div className={css.cardInner}>
        <div className={` ${css.cardIcon} ${css.ciPlan}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
        </div>
        <h3>{t("Get a personal plan")}</h3>
        <p className={css.desc}>{t("Answer a few questions about your goal and level — get a structured course built just for you.")}</p>
        <button className={` ${css.cta} ${css.ctaPrimary}`}
        onClick={() => {
            trackOnboardingStart();
            trackTrialStarted();
            handleNavigation("/survey");
          }}
        >
          {t("Create my plan")}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
        </button>
      </div>
    </div>

    <div className={css.card}>
      <div className={css.cardInner}>
        <div className={` ${css.cardIcon} ${css.ciFree}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/></svg>
        </div>
        <h3>{t("Learn without a plan")}</h3>
        <p className={css.desc}>{t("Browse all videos and choose what you want to practice. Learn any topic, any time — your way.")}</p>
        <button className={` ${css.cta} ${css.ctaSoft}`}
        onClick={() => {
            handleNavigation("/catalog");
          }}>
          {t("Browse videos")}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
        </button>
      </div>
    </div>
  </div>
</section>

<section className={css.twoWays}>
  <div className={` ${css.twoWaysInner} ${css.reveal}`}>
    <h2 className={css.sTitle}>{t("Two ways to learn")}</h2>
    <p className={css.sSub}>{t("Both paths include real-life video lessons and AI speaking practice.")}</p>

    <div className={css.waysGrid}>
      <div className={` ${css.wayCard} ${css.wPlan}`}>
        <div className={css.wayHead}>
          <span className={` ${css.wayDot} ${css.wdIndigo}`}></span>
          <h3>{t("Personal Plan")}</h3>
        </div>
        <ul className={css.wayList}>
          <li><svg className={css.wcIndigo} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>{t("Structured lessons in a clear order")}</li>
          <li><svg className={css.wcIndigo} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>{t("Course based on your goal & level")}</li>
          <li><svg className={css.wcIndigo} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>{t("Level test available if you need it")}</li>
          <li><svg className={css.wcIndigo} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>{t("Track your progress over time")}</li>
        </ul>
      </div>

      <div className={css.switchCol}>
        <div className={css.switchArrows}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
        </div>
        <span className={css.switchLabel}>{t("Switch")}<br/>{t("anytime")}</span>
      </div>

      <div className={` ${css.wayCard} ${css.wFree}`}>
        <div className={css.wayHead}>
          <span className={` ${css.wayDot} ${css.wdViolet}`}></span>
          <h3>{t("Learn Without a Plan")}</h3>
        </div>
        <ul className={css.wayList}>
          <li><svg className={css.wcViolet} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>{t("Choose any video you like")}</li>
          <li><svg className={css.wcViolet} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>{t("Practice any topic freely")}</li>
          <li><svg className={css.wcViolet} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>{t("No fixed order or schedule")}</li>
          <li><svg className={css.wcViolet} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>{t("Switch to a personal plan anytime")}</li>
        </ul>
      </div>
    </div>
  </div>
</section>


<section className={css.afterSection}>
  <div className={css.reveal}>
    <h2 className={css.sTitle}>{t("What happens after the 2-minute lesson?")}</h2>
    <p className={css.sSub}>{t("Complete a quick demo, then choose your path forward.")}</p>

    <div className={css.afterFlow}>
      <div className={`${css.afBox} ${css.afStart}`}>
        <div className={css.afIcn}
        onClick={() => {
            trackDemoStarted();
            handleNavigation("/lesson/774");
          }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </div>
        <div className={css.afTitle}>{t("Try quick lesson")}</div>
        <div className={css.afSub}>{t("2 minutes")}</div>
      </div>

      <div className={css.afConn}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
      </div>

      <div className={css.afChoices}>
        <div className={`${css.afChoice} ${css.cPlan}`}
        onClick={() => {
            trackOnboardingStart();
            trackTrialStarted();
            handleNavigation("/survey");
          }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
          {t("Get a personal plan")}
        </div>
        <div className={`${css.afChoice} ${css.cFree}`}
        onClick={() => {
            handleNavigation("/catalog");
          }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/></svg>
          {t("Continue learning on my own")}
        </div>
      </div>
    </div>
  </div>
</section>
    </main>
  );
};

export default NewFirstBlock;
